import React, { useState, useEffect, useRef, useCallback } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { signOut } from '../store/slices/authSlice'
import { colors } from '../constants/colors'
import Sidebar from '../components/(admin)/Sidebar'
import PageTopBar from '../components/(admin)/PageTopBar'
import { useDashboardSocket } from '../context/DashboardSocketContext'
import {
  getConversations,
  getMessagesByConversation,
  getHumanAgentEnabled,
  getChatSessions,
  ensureChatSession,
  createMessage,
  closeChatSession,
  joinChatSession,
  updateAgentStatus,
  presencePing
} from '../services/(adminapi)/messagesAPI'
const ChatLogs = () => {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const { user } = useSelector((s) => s.auth)
  const { socket } = useDashboardSocket()
  const prevWatchedConvRef = useRef(null)

  const handleSignOut = async () => {
    try {
      await dispatch(signOut()).unwrap()
      navigate('/signin')
    } catch (err) {
      console.error('Logout failed:', err)
    }
  }
  const [selectedChat, setSelectedChat] = useState(null)
  const [isDetailsExpanded, setIsDetailsExpanded] = useState(true)
  const [conversations, setConversations] = useState([])
  const [messages, setMessages] = useState({})
  const [loadingMessages, setLoadingMessages] = useState({})
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [sessionByConversation, setSessionByConversation] = useState({})
  const [replyDraft, setReplyDraft] = useState('')
  const [sendingReply, setSendingReply] = useState(false)
  const [joiningSession, setJoiningSession] = useState(false)
  const [presenceMap, setPresenceMap] = useState({})
  const [humanHandoffEnabled, setHumanHandoffEnabled] = useState(false)

  const isAgentUser = user?.role === 'Agent'
  const selectedConversationId =
    selectedChat !== null && conversations[selectedChat]
      ? String(conversations[selectedChat]._id)
      : null
  const activeChatSession = selectedConversationId
    ? sessionByConversation[selectedConversationId]
    : null

  const canAgentJoinChat = () => {
    if (!humanHandoffEnabled || !isAgentUser || !activeChatSession) return false
    const myId = String(user?.id || '')
    const assignedTo = String(
      activeChatSession.assigned_to?._id ?? activeChatSession.assigned_to ?? ''
    )
    if (activeChatSession.status === 'queued') {
      return !assignedTo || assignedTo === myId
    }
    if (
      activeChatSession.status === 'assigned' &&
      assignedTo === myId &&
      !activeChatSession.agent_joined_at
    ) {
      return true
    }
    return false
  }

  const loadSessions = useCallback(async () => {
    try {
      const res = await getChatSessions()
      if (res.success && Array.isArray(res.data)) {
        const m = {}
        for (const s of res.data) {
          const convId = s.conversation_id?._id ?? s.conversation_id
          if (convId) m[String(convId)] = s
        }
        setSessionByConversation(m)
      }
    } catch (e) {
      console.error('loadSessions', e)
    }
  }, [])

  useEffect(() => {
    fetchConversations()
  }, [])

  useEffect(() => {
    let cancelled = false
    getHumanAgentEnabled()
      .then((res) => {
        if (cancelled || !res?.success) return
        setHumanHandoffEnabled(Boolean(res.data?.human_agent_enabled))
      })
      .catch((e) => console.error('getHumanAgentEnabled', e))
    return () => {
      cancelled = true
    }
  }, [])

  useEffect(() => {
    loadSessions()
  }, [loadSessions])

  useEffect(() => {
    if (!isAgentUser) return
    updateAgentStatus('online')
      .then(() => loadSessions())
      .catch(() => {})
  }, [isAgentUser, loadSessions])

  useEffect(() => {
    const cid = searchParams.get('conversation')
    if (!cid || conversations.length === 0) return
    const idx = conversations.findIndex((c) => String(c._id) === cid)
    if (idx >= 0) setSelectedChat(idx)
  }, [searchParams, conversations])

  useEffect(() => {
    if (!socket) return undefined

    const mergeMessage = (msg) => {
      if (!msg?.conversation_id) return
      const cid = String(msg.conversation_id)
      setMessages((prev) => {
        const list = prev[cid] || []
        if (list.some((m) => String(m._id) === String(msg._id))) return prev
        const next = [...list, msg].sort(
          (a, b) => new Date(a.timestamp) - new Date(b.timestamp)
        )
        return { ...prev, [cid]: next }
      })
    }

    const onLoadSessions = () => {
      loadSessions()
    }

    const onNewMessage = (data) => {
      if (data?.message) {
        mergeMessage(data.message)
        if (data.message.sender_type === 'agent') {
          loadSessions()
        }
      }
    }

    const onPresenceUpdate = (p) => {
      const key = p.userId || p.visitor_id || 'unknown'
      setPresenceMap((prev) => ({ ...prev, [key]: p }))
    }

    socket.on('new_chat_assigned', onLoadSessions)
    socket.on('queue_update', onLoadSessions)
    socket.on('new_message', onNewMessage)
    socket.on('ai_message', onLoadSessions)
    socket.on('chat_assigned', onLoadSessions)
    socket.on('session_queued', onLoadSessions)
    socket.on('handoff_requested', onLoadSessions)
    socket.on('new_handoff_request', onLoadSessions)
    socket.on('next_chat_ready', onLoadSessions)
    socket.on('session_closed', onLoadSessions)
    socket.on('presence_update', onPresenceUpdate)

    return () => {
      socket.off('new_chat_assigned', onLoadSessions)
      socket.off('queue_update', onLoadSessions)
      socket.off('new_message', onNewMessage)
      socket.off('ai_message', onLoadSessions)
      socket.off('chat_assigned', onLoadSessions)
      socket.off('session_queued', onLoadSessions)
      socket.off('handoff_requested', onLoadSessions)
      socket.off('new_handoff_request', onLoadSessions)
      socket.off('next_chat_ready', onLoadSessions)
      socket.off('session_closed', onLoadSessions)
      socket.off('presence_update', onPresenceUpdate)
    }
  }, [socket, loadSessions])

  useEffect(() => {
    const id = setInterval(() => {
      presencePing().catch(() => {})
    }, 30000)
    presencePing().catch(() => {})
    return () => clearInterval(id)
  }, [])

  useEffect(() => {
    const s = socket
    if (!s || !selectedConversationId) return undefined

    const setup = () => {
      const prev = prevWatchedConvRef.current
      if (prev && prev !== selectedConversationId) {
        s.emit('leave_conversation', { conversationId: prev })
      }
      s.emit('watch_conversation', { conversationId: selectedConversationId })
      prevWatchedConvRef.current = selectedConversationId
    }

    if (s.connected) setup()
    else s.once('connect', setup)

    return () => {
      if (s.connected && selectedConversationId) {
        s.emit('leave_conversation', { conversationId: selectedConversationId })
      }
    }
  }, [socket, selectedConversationId])

  // Fetch messages when a conversation is selected
  useEffect(() => {
    if (selectedChat !== null && conversations[selectedChat]) {
      const conversationId = conversations[selectedChat]._id
      // Only fetch if we haven't loaded messages for this conversation yet
      if (!messages[conversationId] && !loadingMessages[conversationId]) {
        fetchMessages(conversationId)
      }
      ensureChatSession(conversationId)
        .then(() => loadSessions())
        .catch(() => {})
    }
  }, [selectedChat, conversations])

  const fetchConversations = async () => {
    try {
      setLoading(true)
      setError(null)
      const response = await getConversations()
      if (response.success && response.data) {
        const sortedConversations = response.data.sort((a, b) => 
          new Date(b.created_at) - new Date(a.created_at)
        )
        setConversations(sortedConversations)
        // Auto-select first conversation if available
        if (sortedConversations.length > 0) {
          setSelectedChat(0)
        }
      }
    } catch (err) {
      console.error('Error fetching conversations:', err)
      setError('Failed to load conversations')
    } finally {
      setLoading(false)
    }
  }

  const fetchMessages = async (conversationId) => {
    try {
      setLoadingMessages(prev => ({ ...prev, [conversationId]: true }))
      const response = await getMessagesByConversation(conversationId, 1, 100)
      if (response.success && response.data) {
        // Sort messages by timestamp (oldest first for display)
        const sortedMessages = response.data.sort((a, b) => 
          new Date(a.timestamp) - new Date(b.timestamp)
        )
        setMessages(prev => ({
          ...prev,
          [conversationId]: sortedMessages
        }))
      }
    } catch (err) {
      console.error('Error fetching messages:', err)
      setError('Failed to load messages')
    } finally {
      setLoadingMessages(prev => ({ ...prev, [conversationId]: false }))
    }
  }

  // Format date helper
  const formatDate = (dateString) => {
    if (!dateString) return ''
    const date = new Date(dateString)
    const months = ['January', 'February', 'March', 'April', 'May', 'June', 
                    'July', 'August', 'September', 'October', 'November', 'December']
    return `${months[date.getMonth()]} ${date.getDate()}, ${date.getFullYear()}`
  }

  // Format time helper
  const formatTime = (dateString) => {
    if (!dateString) return ''
    const date = new Date(dateString)
    let hours = date.getHours()
    const minutes = date.getMinutes()
    const ampm = hours >= 12 ? 'pm' : 'am'
    hours = hours % 12
    hours = hours ? hours : 12
    const minutesStr = minutes < 10 ? `0${minutes}` : minutes
    return `${hours}:${minutesStr} ${ampm}`
  }

  // Get date only for grouping
  const getDateOnly = (dateString) => {
    if (!dateString) return ''
    const date = new Date(dateString)
    const months = ['January', 'February', 'March', 'April', 'May', 'June', 
                    'July', 'August', 'September', 'October', 'November', 'December']
    return `${months[date.getMonth()]} ${date.getDate()}`
  }

  // Calculate duration between first and last message
  const calculateDuration = (conversationId) => {
    const conversationMessages = messages[conversationId]
    if (!conversationMessages || conversationMessages.length === 0) return '0 Minutes'
    
    const firstMessage = conversationMessages[0]
    const lastMessage = conversationMessages[conversationMessages.length - 1]
    const diff = new Date(lastMessage.timestamp) - new Date(firstMessage.timestamp)
    const minutes = Math.floor(diff / 60000)
    return minutes <= 1 ? '1 Minute' : `${minutes} Minutes`
  }

  // Session badge label for chat list
  const getSessionStatusTag = (sess, conversationMessages = []) => {
    const hasAgentMessages = conversationMessages.some((m) => m.sender_type === 'agent')
    if (
      hasAgentMessages ||
      sess?.status === 'assigned' ||
      sess?.status === 'agent' ||
      sess?.agent_joined_at
    ) {
      return 'agent'
    }
    if (!sess) return 'ai'
    return sess.status || 'ai'
  }

  // Get preview text (first user message)
  const getPreview = (conversationId) => {
    const conversationMessages = messages[conversationId]
    if (!conversationMessages || conversationMessages.length === 0) return 'No messages yet'
    const firstUserMessage = conversationMessages.find(msg => msg.sender_type === 'user')
    if (firstUserMessage) {
      return firstUserMessage.content.length > 50 
        ? firstUserMessage.content.substring(0, 50) + '...'
        : firstUserMessage.content
    }
    return 'No user messages'
  }

  // Map sender_type to message type for display
  const getMessageType = (senderType) => {
    if (senderType === 'user') return 'user'
    if (senderType === 'agent') return 'agent'
    return 'system' // ai, system
  }

  const handleJoinChat = async () => {
    if (!activeChatSession?._id) return
    setJoiningSession(true)
    setError(null)
    try {
      await joinChatSession(activeChatSession._id)
      await loadSessions()
      setError(null)
    } catch (e) {
      console.error(e)
      const msg = e.response?.data?.error || 'Could not join chat'
      setError(msg)
    } finally {
      setJoiningSession(false)
    }
  }

  const sendAgentReply = async () => {
    if (!selectedConversationId || !replyDraft.trim()) return
    setSendingReply(true)
    try {
      await createMessage({
        conversation_id: selectedConversationId,
        sender_type: 'agent',
        message_type: 'text',
        content: replyDraft.trim()
      })
      setReplyDraft('')
      await fetchMessages(selectedConversationId)
      await loadSessions()
    } catch (e) {
      console.error(e)
    } finally {
      setSendingReply(false)
    }
  }

  const handleCloseSession = async () => {
    if (!activeChatSession?._id) return
    try {
      await closeChatSession(activeChatSession._id)
      await loadSessions()
    } catch (e) {
      console.error(e)
    }
  }

  // Loading and error styles
  const loadingStyle = {
    padding: '40px',
    textAlign: 'center',
    color: colors.text.secondary
  }

  const errorStyle = {
    padding: '40px',
    textAlign: 'center',
    color: '#ef4444'
  }

  const containerStyle = {
    minHeight: '100vh',
    backgroundColor: colors.background.secondary,
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
    display: 'flex'
  }

  const mainContentStyle = {
    flex: 1,
    marginLeft: '280px',
    padding: '24px',
    backgroundColor: colors.background.secondary
  }

  const headerStyle = {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '32px'
  }

  const headerLeftStyle = {
    display: 'flex',
    alignItems: 'center',
    gap: '16px'
  }

  const headerRightStyle = {
    display: 'flex',
    alignItems: 'center',
    gap: '16px'
  }

  const headerIconStyle = {
    width: '24px',
    height: '24px',
    cursor: 'pointer',
    color: colors.text.secondary
  }

  const notificationIconStyle = {
    ...headerIconStyle,
    position: 'relative'
  }

  const notificationDotStyle = {
    position: 'absolute',
    top: '-2px',
    right: '-2px',
    width: '8px',
    height: '8px',
    backgroundColor: '#ef4444',
    borderRadius: '50%'
  }

  const profileImageStyle = {
    width: '32px',
    height: '32px',
    borderRadius: '50%',
    backgroundColor: colors.primary,
    cursor: 'pointer'
  }

  const titleStyle = {
    fontSize: '28px',
    fontWeight: '700',
    color: colors.text.primary,
    margin: 0
  }

  const filtersStyle = {
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
    marginBottom: '24px'
  }

  const dropdownStyle = {
    padding: '8px 12px',
    border: `1px solid ${colors.borderVariants.light}`,
    borderRadius: '6px',
    backgroundColor: colors.background.primary,
    color: colors.text.primary,
    fontSize: '14px',
    cursor: 'pointer'
  }

  const exportButtonStyle = {
    padding: '8px 16px',
    fontSize: '14px',
    fontWeight: '500',
    color: colors.background.primary,
    backgroundColor: colors.primary,
    border: 'none',
    borderRadius: '6px',
    cursor: 'pointer',
    transition: 'background-color 0.2s ease'
  }

  const chatLayoutStyle = {
    display: 'grid',
    gridTemplateColumns: '1fr 2fr 1fr',
    gap: '24px',
    height: 'calc(100vh - 200px)'
  }

  const chatListStyle = {
    backgroundColor: colors.background.primary,
    borderRadius: '8px',
    padding: '16px',
    overflowY: 'auto',
    border: `1px solid ${colors.borderVariants.light}`
  }

  const chatItemStyle = {
    padding: '16px',
    borderBottom: `1px solid ${colors.borderVariants.light}`,
    cursor: 'pointer',
    transition: 'background-color 0.2s ease'
  }

  const selectedChatItemStyle = {
    ...chatItemStyle,
    backgroundColor: '#e6f3ff',
    borderRadius: '6px',
    marginBottom: '8px'
  }

  const chatItemHeaderStyle = {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '8px'
  }

  const userIdStyle = {
    fontSize: '14px',
    fontWeight: '600',
    color: colors.text.primary,
    margin: 0
  }

  const dateStyle = {
    fontSize: '12px',
    color: colors.text.secondary,
    margin: 0
  }

  const previewStyle = {
    fontSize: '14px',
    color: colors.text.secondary,
    margin: 0,
    lineHeight: '1.4'
  }

  const chatIconStyle = {
    width: '16px',
    height: '16px',
    color: colors.text.secondary
  }

  const conversationStyle = {
    backgroundColor: colors.background.primary,
    borderRadius: '8px',
    padding: '24px',
    overflowY: 'auto',
    border: `1px solid ${colors.borderVariants.light}`
  }

  const conversationHeaderStyle = {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '24px',
    paddingBottom: '16px',
    borderBottom: `1px solid ${colors.borderVariants.light}`
  }

  const conversationTitleStyle = {
    fontSize: '18px',
    fontWeight: '600',
    color: colors.text.primary,
    margin: 0
  }

  const dateSeparatorStyle = {
    display: 'flex',
    alignItems: 'center',
    margin: '24px 0',
    color: colors.text.secondary,
    fontSize: '14px'
  }

  const separatorLineStyle = {
    flex: 1,
    height: '1px',
    backgroundColor: colors.borderVariants.light,
    margin: '0 16px'
  }

  const messageStyle = {
    marginBottom: '16px',
    display: 'flex',
    flexDirection: 'column'
  }

  const userMessageStyle = {
    alignSelf: 'flex-start',
    maxWidth: '70%'
  }

  const systemMessageStyle = {
    alignSelf: 'flex-end',
    maxWidth: '70%'
  }

  const messageBubbleStyle = {
    padding: '12px 16px',
    borderRadius: '18px',
    fontSize: '14px',
    lineHeight: '1.4',
    marginBottom: '4px'
  }

  const userBubbleStyle = {
    ...messageBubbleStyle,
    backgroundColor: colors.background.primary,
    border: `1px solid ${colors.borderVariants.light}`,
    color: colors.text.primary
  }

  const systemBubbleStyle = {
    ...messageBubbleStyle,
    backgroundColor: '#e6f3ff',
    color: colors.text.primary
  }

  const agentBubbleStyle = {
    ...messageBubbleStyle,
    backgroundColor: '#dcfce7',
    color: colors.text.primary
  }

  const messageTimeStyle = {
    fontSize: '12px',
    color: colors.text.secondary,
    margin: 0
  }

  const detailsStyle = {
    backgroundColor: colors.background.primary,
    borderRadius: '8px',
    padding: '24px',
    border: `1px solid ${colors.borderVariants.light}`
  }

  const detailsHeaderStyle = {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '20px',
    cursor: 'pointer'
  }

  const detailsTitleStyle = {
    fontSize: '18px',
    fontWeight: '600',
    color: colors.text.primary,
    margin: 0
  }

  const chevronStyle = {
    width: '20px',
    height: '20px',
    color: colors.text.secondary,
    transform: isDetailsExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
    transition: 'transform 0.2s ease'
  }

  const detailsContentStyle = {
    display: isDetailsExpanded ? 'block' : 'none'
  }

  const detailItemStyle = {
    marginBottom: '12px'
  }

  const detailLabelStyle = {
    fontSize: '12px',
    fontWeight: '500',
    color: colors.text.secondary,
    margin: '0 0 4px 0',
    textTransform: 'uppercase'
  }

  const detailValueStyle = {
    fontSize: '14px',
    color: colors.text.primary,
    margin: 0
  }

  const actionsStyle = {
    marginTop: '24px',
    paddingTop: '20px',
    borderTop: `1px solid ${colors.borderVariants.light}`
  }

  const deleteButtonStyle = {
    padding: '8px 16px',
    fontSize: '14px',
    fontWeight: '500',
    color: 'white',
    backgroundColor: '#ef4444',
    border: 'none',
    borderRadius: '6px',
    cursor: 'pointer',
    transition: 'background-color 0.2s ease'
  }

  return (
    <div style={containerStyle}>
      {/* Sidebar */}
      <Sidebar />
      
      {/* Main Content */}
      <div style={mainContentStyle}>
        {/* Header */}
        <PageTopBar
          title="Chat Logs"
          onAiConfig={() => navigate('/ai-configuration')}
          onEmbeddingGuide={() => navigate('/embedding-guide')}
          onSignOut={handleSignOut}
        />

        {humanHandoffEnabled && (
          <div
            style={{
              marginBottom: '16px',
              padding: '12px 16px',
              backgroundColor: colors.background.primary,
              border: `1px solid ${colors.borderVariants.light}`,
              borderRadius: '8px',
              display: 'flex',
              alignItems: 'center',
              gap: '16px',
              flexWrap: 'wrap'
            }}
          >
            <span style={{ fontSize: '14px', color: colors.text.secondary }}>
              Human agent handoff is enabled for this business.
              {isAgentUser && ' You are available for live chats while logged in.'}
            </span>
          </div>
        )}

        {/* Filters */}
        <div style={filtersStyle}>
          <select style={dropdownStyle}>
            <option>Last 7 days</option>
            <option>Last 30 days</option>
            <option>Last 90 days</option>
          </select>
          <select style={dropdownStyle}>
            <option>All Users</option>
            <option>User 123</option>
            <option>User 456</option>
            <option>User 789</option>
          </select>
          <select style={dropdownStyle}>
            <option>All</option>
            <option>Resolved</option>
            <option>In Progress</option>
            <option>Fallback</option>
          </select>
          <select style={dropdownStyle}>
            <option>Upload Data</option>
            <option>Import CSV</option>
            <option>Export Data</option>
          </select>
          <button style={exportButtonStyle}>Export</button>
        </div>

        {/* Chat Layout */}
        {loading ? (
          <div style={loadingStyle}>Loading conversations...</div>
        ) : error ? (
          <div style={errorStyle}>{error}</div>
        ) : conversations.length === 0 ? (
          <div style={loadingStyle}>No conversations found</div>
        ) : (
          <div style={chatLayoutStyle}>
            {/* Left Panel - Chat List */}
            <div style={chatListStyle}>
              {conversations.map((conversation, index) => {
                const conversationId = conversation._id
                const conversationMessages = messages[conversationId] || []
                const preview = getPreview(conversationId)
                const sess = sessionByConversation[String(conversationId)]
                const statusTag = getSessionStatusTag(sess, conversationMessages)
                
                return (
                  <div
                    key={conversation._id}
                    style={selectedChat === index ? selectedChatItemStyle : chatItemStyle}
                    onClick={() => setSelectedChat(index)}
                    onMouseEnter={(e) => {
                      if (selectedChat !== index) {
                        e.currentTarget.style.backgroundColor = colors.background.secondary
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (selectedChat !== index) {
                        e.currentTarget.style.backgroundColor = 'transparent'
                      }
                    }}
                  >
                    <div style={chatItemHeaderStyle}>
                      <div>
                        <h3 style={userIdStyle}>
                          {conversation.contact_id || `User ${conversation._id.toString().substring(0, 8)}`}
                        </h3>
                        <p style={dateStyle}>
                          {formatDate(conversation.created_at)}
                          {humanHandoffEnabled && (
                            <span
                              style={{
                                marginLeft: '8px',
                                fontSize: '11px',
                                padding: '2px 6px',
                                borderRadius: '4px',
                                backgroundColor: '#e2e8f0',
                                color: '#334155'
                              }}
                            >
                              {statusTag}
                            </span>
                          )}
                        </p>
                      </div>
                      <svg style={chatIconStyle} fill="currentColor" viewBox="0 0 24 24">
                        <path d="M20 2H4c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h4l4 4 4-4h4c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2zm-2 12H6v-2h12v2zm0-3H6V9h12v2zm0-3H6V6h12v2z"/>
                      </svg>
                    </div>
                    <p style={previewStyle}>{preview}</p>
                  </div>
                )
              })}
            </div>

            {/* Middle Panel - Conversation */}
            {selectedChat !== null && conversations[selectedChat] && (
              <>
                <div style={conversationStyle}>
                  <div style={conversationHeaderStyle}>
                    <div>
                      <h2 style={conversationTitleStyle}>
                        {conversations[selectedChat].contact_id || `User ${conversations[selectedChat]._id.toString().substring(0, 8)}`}
                      </h2>
                      {humanHandoffEnabled && activeChatSession && (
                        <p style={{ margin: '4px 0 0 0', fontSize: '13px', color: colors.text.secondary }}>
                          Session: {activeChatSession.status}
                          {activeChatSession.assigned_to?.name &&
                            ` · Agent: ${activeChatSession.assigned_to.name}`}
                        </p>
                      )}
                    </div>
                    <p style={dateStyle}>{formatDate(conversations[selectedChat].created_at)}</p>
                  </div>

                  {loadingMessages[conversations[selectedChat]._id] ? (
                    <div style={loadingStyle}>Loading messages...</div>
                  ) : messages[conversations[selectedChat]._id] && messages[conversations[selectedChat]._id].length > 0 ? (
                    messages[conversations[selectedChat]._id].map((message, index) => {
                      const messageDate = getDateOnly(message.timestamp)
                      const prevMessageDate = index > 0 ? getDateOnly(messages[conversations[selectedChat]._id][index - 1].timestamp) : null
                      const showDateSeparator = index === 0 || prevMessageDate !== messageDate
                      const messageType = getMessageType(message.sender_type)

                      return (
                        <div key={message._id || index}>
                          {showDateSeparator && (
                            <div style={dateSeparatorStyle}>
                              <div style={separatorLineStyle}></div>
                              <span>{messageDate}</span>
                              <div style={separatorLineStyle}></div>
                            </div>
                          )}
                          <div style={messageStyle}>
                            <div
                              style={
                                messageType === 'user' ? userMessageStyle : systemMessageStyle
                              }
                            >
                              <div
                                style={
                                  messageType === 'user'
                                    ? userBubbleStyle
                                    : messageType === 'agent'
                                      ? agentBubbleStyle
                                      : systemBubbleStyle
                                }
                              >
                                {message.content}
                              </div>
                              <p style={messageTimeStyle}>{formatTime(message.timestamp)}</p>
                            </div>
                          </div>
                        </div>
                      )
                    })
                  ) : (
                    <div style={loadingStyle}>No messages in this conversation</div>
                  )}

                  {canAgentJoinChat() && (
                      <div
                        style={{
                          marginTop: '16px',
                          paddingTop: '16px',
                          borderTop: `1px solid ${colors.borderVariants.light}`
                        }}
                      >
                        <button
                          type="button"
                          disabled={joiningSession}
                          onClick={handleJoinChat}
                          style={{
                            padding: '8px 16px',
                            borderRadius: '6px',
                            border: 'none',
                            backgroundColor: colors.primary,
                            color: '#fff',
                            cursor: joiningSession ? 'wait' : 'pointer',
                            fontWeight: 600
                          }}
                        >
                          {joiningSession ? 'Joining…' : 'Join chat'}
                        </button>
                      </div>
                    )}

                  {humanHandoffEnabled &&
                    isAgentUser &&
                    activeChatSession?.status === 'assigned' &&
                    String(activeChatSession.assigned_to?._id || activeChatSession.assigned_to) !==
                      String(user?.id) && (
                      <p style={{ marginTop: '16px', fontSize: '14px', color: colors.text.secondary }}>
                        This chat is assigned to another agent.
                      </p>
                    )}

                  {humanHandoffEnabled &&
                    isAgentUser &&
                    activeChatSession?.status === 'assigned' &&
                    String(activeChatSession.assigned_to?._id || activeChatSession.assigned_to) ===
                      String(user?.id) && (
                      <div
                        style={{
                          marginTop: '16px',
                          paddingTop: '16px',
                          borderTop: `1px solid ${colors.borderVariants.light}`
                        }}
                      >
                        <textarea
                          value={replyDraft}
                          onChange={(e) => setReplyDraft(e.target.value)}
                          placeholder="Type a reply as agent..."
                          rows={3}
                          style={{
                            width: '100%',
                            padding: '10px',
                            borderRadius: '8px',
                            border: `1px solid ${colors.borderVariants.light}`,
                            fontSize: '14px',
                            resize: 'vertical',
                            marginBottom: '8px'
                          }}
                        />
                        <button
                          type="button"
                          disabled={sendingReply || !replyDraft.trim()}
                          onClick={sendAgentReply}
                          style={{
                            padding: '8px 16px',
                            borderRadius: '6px',
                            border: 'none',
                            backgroundColor: colors.primary,
                            color: '#fff',
                            cursor: sendingReply ? 'wait' : 'pointer',
                            fontWeight: 600
                          }}
                        >
                          {sendingReply ? 'Sending…' : 'Send as agent'}
                        </button>
                      </div>
                    )}

                  {humanHandoffEnabled &&
                    activeChatSession &&
                    activeChatSession.status !== 'closed' &&
                    (user?.role === 'Owner' ||
                      String(activeChatSession.assigned_to?._id || activeChatSession.assigned_to) ===
                        String(user?.id)) && (
                      <div style={{ marginTop: '12px' }}>
                        <button
                          type="button"
                          onClick={handleCloseSession}
                          style={{
                            padding: '8px 14px',
                            borderRadius: '6px',
                            border: `1px solid ${colors.borderVariants.light}`,
                            backgroundColor: '#fff',
                            cursor: 'pointer',
                            fontSize: '13px'
                          }}
                        >
                          Close / release chat session
                        </button>
                      </div>
                    )}
                </div>

                {/* Right Panel - Details */}
                <div style={detailsStyle}>
                  <div 
                    style={detailsHeaderStyle}
                    onClick={() => setIsDetailsExpanded(!isDetailsExpanded)}
                  >
                    <h3 style={detailsTitleStyle}>Details</h3>
                    <svg style={chevronStyle} fill="currentColor" viewBox="0 0 24 24">
                      <path d="M7.41 8.59L12 13.17l4.59-4.58L18 10l-6 6-6-6 1.41-1.41z"/>
                    </svg>
                  </div>
                  
                  <div style={detailsContentStyle}>
                    <div style={detailItemStyle}>
                      <p style={detailLabelStyle}>User ID</p>
                      <p style={detailValueStyle}>
                        {conversations[selectedChat].contact_id || `User ${conversations[selectedChat]._id.toString().substring(0, 8)}`}
                      </p>
                    </div>
                    <div style={detailItemStyle}>
                      <p style={detailLabelStyle}>Session ID</p>
                      <p style={detailValueStyle}>{conversations[selectedChat]._id.toString().substring(0, 12)}</p>
                    </div>
                    <div style={detailItemStyle}>
                      <p style={detailLabelStyle}>Started</p>
                      <p style={detailValueStyle}>
                        {formatDate(conversations[selectedChat].created_at)}, {formatTime(conversations[selectedChat].created_at)}
                      </p>
                    </div>
                    <div style={detailItemStyle}>
                      <p style={detailLabelStyle}>Duration</p>
                      <p style={detailValueStyle}>
                        {calculateDuration(conversations[selectedChat]._id)}
                      </p>
                    </div>
                    <div style={detailItemStyle}>
                      <p style={detailLabelStyle}>Channel</p>
                      <p style={detailValueStyle}>{conversations[selectedChat].channel || 'website_chat'}</p>
                    </div>
                    {humanHandoffEnabled && activeChatSession && (
                      <>
                        <div style={detailItemStyle}>
                          <p style={detailLabelStyle}>Chat session status</p>
                          <p style={detailValueStyle}>{activeChatSession.status}</p>
                        </div>
                        <div style={detailItemStyle}>
                          <p style={detailLabelStyle}>Handoff</p>
                          <p style={detailValueStyle}>
                            {activeChatSession.handoff_required || activeChatSession.handoff
                              ? 'Yes'
                              : 'No'}
                          </p>
                        </div>
                        {activeChatSession.assigned_to?._id && (
                          <div style={detailItemStyle}>
                            <p style={detailLabelStyle}>Assigned agent (pulse)</p>
                            <p style={detailValueStyle}>
                              {presenceMap[String(activeChatSession.assigned_to._id)]?.is_online
                                ? 'Online'
                                : 'Unknown / offline'}
                            </p>
                          </div>
                        )}
                        {user?.id && (
                          <div style={detailItemStyle}>
                            <p style={detailLabelStyle}>You (pulse)</p>
                            <p style={detailValueStyle}>
                              {presenceMap[String(user.id)]?.is_online ? 'Online' : '—'}
                            </p>
                          </div>
                        )}
                      </>
                    )}
                  </div>

                  {/* <div style={actionsStyle}>
                    <button style={deleteButtonStyle}>Delete Log</button>
                  </div> */}
                </div>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  )
}

export default ChatLogs
